import { Grid, Text } from '@raskrask-dk/rr-components'
import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledCompanyLeadSummary } from './styles'
import { PropsCompanyLeadSummaryTemplate } from './props'

const SummaryText = ({
    question,
    answer,
}: {
    question: string
    answer: ReactNode
}) => (
    <Grid>
        <Text primary size="s">
            {question}
        </Text>
        <Text primary bold>
            {answer}
        </Text>
    </Grid>
)

const CompanyLeadSummaryTemplate = ({
    companySettings,
}: PropsCompanyLeadSummaryTemplate): ReactElement => {
    const { t } = useTranslation()

    // Map to locize keys
    const frequencyLanguageKeys = {
        Ugentlig: 'companylead.frequency.options.weekly',
        'Hver 2. uge': 'companylead.frequency.options.secondweek',
        Månedligt: 'companylead.frequency.options.monthly',
        Andet: 'companylead.frequency.options.other',
    }

    const towelLanguageKeys = {
        MON: 'common:days.monday.long',
        TUE: 'common:days.tuesday.long',
        WED: 'common:days.wednesday.long',
        THU: 'common:days.thursday.long',
        FRI: 'common:days.friday.long',
    }

    const financeLanguageKeys = {
        CompanyFull: 'companylead.financing.companyfull.label',
        Employee: 'companylead.financing.employeefull.label',
        Shared: 'companylead.financing.shared.label',
        Bruttolønsordning: 'companylead.financing.grosspay.label',
    }

    const leadSummaries = [
        {
            question: t(`companylead.summary.questions.frequency`),
            answer: t(
                frequencyLanguageKeys[companySettings?.frequency || 'Andet']
            ),
        },
        {
            question: t(`companylead.summary.questions.wantedweekdays`),
            answer: companySettings?.wantedWeekDays
                ?.map((day) => t(towelLanguageKeys[day]))
                .join(', '),
        },
        {
            question: t(`companylead.summary.questions.numberofpeople`),
            answer: companySettings?.amountOfPeople
                ? companySettings.amountOfPeople
                : undefined,
        },
        {
            question: t(
                `companylead.summary.questions.earlieststartandlatestend`
            ),
            answer:
                companySettings?.wantedFromTime && companySettings?.wantedToTime
                    ? `${
                          companySettings?.wantedFromTime ??
                          t('companylead.startend.choosenone')
                      } - ${
                          companySettings?.wantedToTime ??
                          t('companylead.startend.choosenone')
                      }`
                    : undefined,
        },
        {
            question: t(`companylead.summary.questions.paymentsolution`),
            answer: t(
                financeLanguageKeys[
                    companySettings?.paymentSolution || 'Bruttolønsordning'
                ]
            ),
        },
        {
            question: t(`companylead.summary.questions.comments`),
            answer: companySettings?.comments,
        },
    ]

    const leadSummaryDiv = leadSummaries.map(
        (summary, key) =>
            summary.answer && (
                <SummaryText
                    key={key}
                    question={summary.question}
                    answer={summary.answer}
                />
            )
    )

    return (
        <StyledCompanyLeadSummary>
            <Grid gap={16}>{leadSummaryDiv}</Grid>
        </StyledCompanyLeadSummary>
    )
}

export default CompanyLeadSummaryTemplate
